import React, { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Team from '../components/Team';
import {
  careers,
  diversityCommitments,
  firmStats,
  insights,
  legalPages,
  offices,
  practiceAreas,
  resources,
  sectors,
  values
} from '../data/siteData';
import './Pages.css';

const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.65 }
};

const ArrowIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M3 9H15M15 9L10 4M15 9L10 14" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const PageHero = ({ label, title, accent, description, stats, actions }) => (
  <section className="page-hero">
    <div className="page-hero-bg" />
    <div className="section-container page-hero-content">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.75 }}
      >
        <span className="section-label">{label}</span>
        <h1 className="page-title">
          {title}
          {accent && (
            <>
              <br />
              <span>{accent}</span>
            </>
          )}
        </h1>
        <p className="page-description">{description}</p>
        {actions && <div className="page-hero-actions">{actions}</div>}
      </motion.div>

      {stats && (
        <motion.div
          className="page-stat-grid"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.15 }}
        >
          {stats.map((stat) => (
            <div key={`${stat.value}-${stat.label}`} className="page-stat">
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </motion.div>
      )}
    </div>
  </section>
);

const CTASection = ({ title, text, to = '/contact', label = 'Start a Conversation' }) => (
  <section className="page-cta">
    <div className="section-container page-cta-inner">
      <div>
        <span className="section-label">NEXT STEP</span>
        <h2>{title}</h2>
        <p>{text}</p>
      </div>
      <Link to={to} className="solid-action">
        {label}
        <ArrowIcon />
      </Link>
    </div>
  </section>
);

const DetailList = ({ title, items }) => (
  <div className="detail-panel">
    <h2>{title}</h2>
    <ul className="check-list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  </div>
);

const NotFoundContent = () => (
  <>
    <PageHero
      label="PAGE NOT FOUND"
      title="This page has moved"
      accent="or does not exist"
      description="The page you requested is not available. You can return to the homepage or explore our core services."
      actions={
        <>
          <Link to="/" className="solid-action">
            Home
            <ArrowIcon />
          </Link>
          <Link to="/services" className="outline-action">
            Services
          </Link>
        </>
      }
    />
  </>
);

export const PracticeAreasPage = () => (
  <>
    <PageHero
      label="PRACTICE AREAS"
      title="Legal capability"
      accent="for complex matters"
      description="A connected platform for transactions, disputes, regulation, finance, real estate, and technology. Each team is built to work across borders and disciplines."
      stats={[
        { value: '6', label: 'Core practices' },
        { value: '31', label: 'Office network' },
        { value: '45+', label: 'Jurisdictions coordinated' }
      ]}
    />

    <section className="page-section">
      <div className="section-container">
        <div className="page-card-grid three">
          {practiceAreas.map((area, index) => (
            <motion.div key={area.slug} {...fadeUp} transition={{ duration: 0.6, delay: index * 0.06 }}>
              <Link to={`/services/${area.slug}`} className="page-card">
                <span className="card-icon">{area.icon}</span>
                <h2>{area.title}</h2>
                <p>{area.summary}</p>
                <span className="text-link">
                  Explore practice
                  <ArrowIcon size={16} />
                </span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="page-section muted">
      <div className="section-container split-section">
        <motion.div {...fadeUp}>
          <span className="section-label">HOW WE WORK</span>
          <h2 className="page-section-title">One team around the matter</h2>
        </motion.div>
        <motion.div className="rich-copy" {...fadeUp}>
          <p>
            Our matters are staffed around the client outcome rather than an internal chart. Corporate, finance,
            regulatory, disputes, employment, tax, and technology lawyers work from the same plan, timetable, and risk map.
          </p>
          <p>
            That structure helps clients move faster without losing judgement, documentation quality, or local market nuance.
          </p>
        </motion.div>
      </div>
    </section>

    <CTASection
      title="Bring us into the conversation early."
      text="We can help scope the legal workstreams, approvals, diligence, and execution path for your next strategic matter."
    />
  </>
);

export const PracticeAreaDetailPage = () => {
  const { slug } = useParams();
  const area = practiceAreas.find((item) => item.slug === slug);

  if (!area) {
    return <NotFoundContent />;
  }

  return (
    <>
      <PageHero
        label="PRACTICE AREA"
        title={area.title}
        accent="with global execution"
        description={area.description}
        stats={area.stats}
        actions={
          <>
            <a href={`mailto:${area.contact}`} className="solid-action">
              Email the Team
              <ArrowIcon />
            </a>
            <Link to="/services" className="outline-action">
              All Practices
            </Link>
          </>
        }
      />

      <section className="page-section">
        <div className="section-container detail-layout">
          <div className="detail-main">
            <DetailList title="How We Help" items={area.services} />
            <div className="detail-panel">
              <h2>Representative Work</h2>
              <div className="matter-list">
                {area.matters.map((matter) => (
                  <article key={matter} className="matter-item">
                    <p>{matter}</p>
                  </article>
                ))}
              </div>
            </div>
          </div>

          <aside className="detail-aside">
            <div className="aside-block">
              <span className="aside-kicker">Specialties</span>
              <div className="tag-list">
                {area.specialties.map((specialty) => (
                  <span key={specialty}>{specialty}</span>
                ))}
              </div>
            </div>
            <div className="aside-block">
              <span className="aside-kicker">Lead Contact</span>
              <a href={`mailto:${area.contact}`} className="aside-link">
                {area.contact}
              </a>
            </div>
            <div className="aside-block">
              <span className="aside-kicker">Related Sectors</span>
              {sectors.slice(0, 3).map((sector) => (
                <Link key={sector.slug} to={`/sectors/${sector.slug}`} className="aside-row">
                  {sector.title}
                  <ArrowIcon size={14} />
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>

      <CTASection
        title={`Discuss ${area.shortTitle.toLowerCase()} strategy with us.`}
        text="Our team can help pressure-test structure, risk, timing, governance, and execution before momentum is expensive to change."
      />
    </>
  );
};

export const SectorsPage = () => (
  <>
    <PageHero
      label="SECTORS"
      title="Industry depth"
      accent="behind every answer"
      description="Our sector teams combine legal skill with market context, technical fluency, and regulator awareness."
      stats={[
        { value: '4', label: 'Priority sectors' },
        { value: '500+', label: 'Financial services matters' },
        { value: '$200B+', label: 'Projects supported' }
      ]}
    />

    <section className="page-section">
      <div className="section-container page-card-grid two">
        {sectors.map((sector, index) => (
          <motion.div key={sector.slug} {...fadeUp} transition={{ duration: 0.6, delay: index * 0.08 }}>
            <Link to={`/sectors/${sector.slug}`} className="wide-card">
              <div>
                <span className="card-icon">{sector.icon}</span>
                <h2>{sector.title}</h2>
                <p>{sector.summary}</p>
              </div>
              <div className="wide-card-meta">
                <strong>{sector.metric}</strong>
                <span className="text-link">
                  Explore sector
                  <ArrowIcon size={16} />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>

    <CTASection
      title="Industry context changes the legal answer."
      text="Connect with a sector team that understands your market, your regulators, and your pressure points."
    />
  </>
);

export const SectorDetailPage = () => {
  const { slug } = useParams();
  const sector = sectors.find((item) => item.slug === slug);

  if (!sector) {
    return <NotFoundContent />;
  }

  return (
    <>
      <PageHero
        label="SECTOR"
        title={sector.title}
        accent="market-focused counsel"
        description={sector.description}
        stats={[
          { value: sector.metric.split(' ')[0], label: sector.metric.replace(sector.metric.split(' ')[0], '').trim() || 'Sector matters' },
          { value: '31', label: 'Offices available' },
          { value: '4', label: 'Integrated disciplines' }
        ]}
      />

      <section className="page-section">
        <div className="section-container detail-layout">
          <div className="detail-main">
            <DetailList title="Sector Capabilities" items={sector.capabilities} />
            <DetailList title="Current Market Themes" items={sector.trends} />
          </div>
          <aside className="detail-aside">
            <div className="aside-block">
              <span className="aside-kicker">Common Workstreams</span>
              {practiceAreas.slice(0, 4).map((area) => (
                <Link key={area.slug} to={`/services/${area.slug}`} className="aside-row">
                  {area.title}
                  <ArrowIcon size={14} />
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export const InsightsPage = () => (
  <>
    <PageHero
      label="INSIGHTS"
      title="Analysis for"
      accent="decision makers"
      description="Briefings, market views, and practical guidance from our lawyers across practices and sectors."
    />

    <section className="page-section">
      <div className="section-container page-card-grid three">
        {insights.map((article, index) => (
          <motion.article key={article.slug} {...fadeUp} transition={{ duration: 0.6, delay: index * 0.05 }}>
            <Link to={`/insights/${article.slug}`} className="article-card">
              <div className="article-visual">
                <span>{article.icon}</span>
                <strong>{article.category}</strong>
              </div>
              <div className="article-body">
                <div className="article-meta">
                  <span>{article.date}</span>
                  <span>|</span>
                  <span>{article.readTime}</span>
                </div>
                <h2>{article.title}</h2>
                <p>{article.excerpt}</p>
                <span className="text-link">
                  Read article
                  <ArrowIcon size={16} />
                </span>
              </div>
            </Link>
          </motion.article>
        ))}
      </div>
    </section>

    <NewsletterBlock />
  </>
);

export const InsightArticlePage = () => {
  const { slug } = useParams();
  const article = insights.find((item) => item.slug === slug);

  if (!article) {
    return <NotFoundContent />;
  }

  return (
    <>
      <PageHero
        label={article.category}
        title={article.title}
        description={article.excerpt}
        stats={[
          { value: article.date, label: 'Published' },
          { value: article.readTime.split(' ')[0], label: 'Minute read' },
          { value: article.category, label: 'Topic' }
        ]}
      />

      <section className="page-section">
        <div className="section-container article-layout">
          <aside className="article-aside">
            <span className="aside-kicker">Author</span>
            <strong>{article.author}</strong>
            <span className="aside-kicker">Related</span>
            {insights
              .filter((item) => item.slug !== article.slug)
              .slice(0, 3)
              .map((item) => (
                <Link key={item.slug} to={`/insights/${item.slug}`} className="aside-row">
                  {item.title}
                </Link>
              ))}
          </aside>
          <article className="article-content">
            {article.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </article>
        </div>
      </section>

      <NewsletterBlock />
    </>
  );
};

const NewsletterBlock = () => {
  const [submitted, setSubmitted] = useState(false);

  return (
    <section className="page-section compact">
      <div className="section-container">
        <form
          className="newsletter-panel"
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(true);
          }}
        >
          <div>
            <span className="section-label">NEWSLETTER</span>
            <h2>Receive our latest briefings</h2>
            <p>Market analysis, legal updates, and practical guidance from Kings Bridge LLP.</p>
          </div>
          <div className="newsletter-controls">
            <input type="email" required placeholder="Email address" aria-label="Email address" />
            <button type="submit" className="solid-action">
              Subscribe
            </button>
            {submitted && <span className="form-note">Thank you. Your subscription request has been recorded.</span>}
          </div>
        </form>
      </div>
    </section>
  );
};

export const AboutPage = () => (
  <>
    <PageHero
      label="ABOUT"
      title="A global firm"
      accent="built for consequential work"
      description="Kings Bridge LLP brings together senior judgement, disciplined execution, and connected teams across major commercial centers."
      stats={firmStats}
    />

    <section className="page-section">
      <div className="section-container split-section">
        <motion.div {...fadeUp}>
          <span className="section-label">OUR FIRM</span>
          <h2 className="page-section-title">Clear advice when the stakes are high</h2>
        </motion.div>
        <motion.div className="rich-copy" {...fadeUp}>
          <p>
            We advise clients on business-critical matters where legal analysis has to connect with capital, reputation,
            people, technology, and time. Our teams are built to move from strategy to execution without losing the detail.
          </p>
          <p>
            The firm pairs global reach with senior attention. Clients work with lawyers who understand the commercial
            context and remain accountable through delivery.
          </p>
        </motion.div>
      </div>
    </section>

    <section className="page-section muted">
      <div className="section-container">
        <div className="page-card-grid four">
          {values.map((value, index) => (
            <motion.div key={value.title} className="value-card" {...fadeUp} transition={{ duration: 0.6, delay: index * 0.05 }}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{value.title}</h3>
              <p>{value.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <Team />
  </>
);

export const CareersPage = () => (
  <>
    <PageHero
      label="CAREERS"
      title="Do meaningful work"
      accent="with demanding clients"
      description="Build a career around excellent legal work, close mentorship, international matters, and a culture that values clarity and responsibility."
      stats={[
        { value: '4', label: 'Featured openings' },
        { value: '31', label: 'Office network' },
        { value: '1', label: 'Connected culture' }
      ]}
    />

    <section className="page-section">
      <div className="section-container page-card-grid two">
        {careers.map((role, index) => (
          <motion.article key={role.slug} className="job-card" {...fadeUp} transition={{ duration: 0.6, delay: index * 0.06 }}>
            <span className="job-team">{role.team}</span>
            <h2>{role.title}</h2>
            <div className="job-meta">
              <span>{role.location}</span>
              <span>{role.type}</span>
            </div>
            <p>{role.summary}</p>
            <ul className="mini-list">
              {role.requirements.map((requirement) => (
                <li key={requirement}>{requirement}</li>
              ))}
            </ul>
            <a href={`mailto:careers@kingsbridgellp.com?subject=${encodeURIComponent(role.title)}`} className="text-link">
              Apply by email
              <ArrowIcon size={16} />
            </a>
          </motion.article>
        ))}
      </div>
    </section>

    <section className="page-section muted">
      <div className="section-container split-section">
        <div>
          <span className="section-label">LIFE AT KINGS BRIDGE</span>
          <h2 className="page-section-title">Training, sponsorship, and room to lead</h2>
        </div>
        <div className="rich-copy">
          <p>
            We invest in structured feedback, technical training, international matter exposure, and leadership opportunities
            for lawyers and business professionals.
          </p>
          <p>
            Candidates can contact our recruitment team for current opportunities, lateral hiring, internships, and business
            services roles.
          </p>
        </div>
      </div>
    </section>
  </>
);

export const LocationsPage = () => (
  <>
    <PageHero
      label="LOCATIONS"
      title="Key offices"
      accent="connected worldwide"
      description="Our office network supports clients across major financial, regulatory, infrastructure, and technology markets."
      stats={[
        { value: '31', label: 'Offices worldwide' },
        { value: '6', label: 'Key hubs shown' },
        { value: '3', label: 'Regional platforms' }
      ]}
    />

    <section className="page-section">
      <div className="section-container page-card-grid three">
        {offices.map((office, index) => (
          <motion.div key={office.slug} {...fadeUp} transition={{ duration: 0.6, delay: index * 0.05 }}>
            <Link to={`/locations/${office.slug}`} className="office-card">
              <span>{office.region}</span>
              <h2>{office.city}</h2>
              <p>{office.description}</p>
              <strong>{office.country}</strong>
              <span className="text-link">
                View office
                <ArrowIcon size={16} />
              </span>
            </Link>
          </motion.div>
        ))}
      </div>
    </section>
  </>
);

export const LocationDetailPage = () => {
  const { slug } = useParams();
  const office = offices.find((item) => item.slug === slug);

  if (!office) {
    return <NotFoundContent />;
  }

  return (
    <>
      <PageHero
        label="OFFICE"
        title={office.city}
        accent={office.country}
        description={office.description}
        stats={[
          { value: office.region, label: 'Region' },
          { value: '4', label: 'Highlighted services' },
          { value: '24h', label: 'Global handover' }
        ]}
      />

      <section className="page-section">
        <div className="section-container detail-layout">
          <div className="detail-main">
            <div className="detail-panel">
              <h2>Office Details</h2>
              <div className="contact-lines">
                <p>{office.address}</p>
                <a href={`tel:${office.phone}`}>{office.phone}</a>
                <a href={`mailto:${office.email}`}>{office.email}</a>
              </div>
            </div>
            <DetailList title="Key Capabilities" items={office.highlights} />
          </div>
          <aside className="detail-aside">
            <div className="aside-block">
              <span className="aside-kicker">All Offices</span>
              {offices.map((item) => (
                <Link key={item.slug} to={`/locations/${item.slug}`} className="aside-row">
                  {item.city}
                  <ArrowIcon size={14} />
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export const ContactPage = () => {
  const [sent, setSent] = useState(false);

  return (
    <>
      <PageHero
        label="CONTACT"
        title="Start the"
        accent="right conversation"
        description="Tell us about the matter, office, or team you need. We will route the inquiry to the appropriate group."
      />

      <section className="page-section">
        <div className="section-container contact-layout">
          <form
            className="contact-form"
            onSubmit={(event) => {
              event.preventDefault();
              setSent(true);
            }}
          >
            <label>
              Name
              <input required type="text" name="name" />
            </label>
            <label>
              Email
              <input required type="email" name="email" />
            </label>
            <label>
              Organization
              <input type="text" name="organization" />
            </label>
            <label>
              Topic
              <select name="topic" defaultValue="General inquiry">
                <option>General inquiry</option>
                <option>Corporate & M&A</option>
                <option>Banking & Finance</option>
                <option>Litigation & Disputes</option>
                <option>Technology & Innovation</option>
                <option>Careers</option>
              </select>
            </label>
            <label className="full">
              Message
              <textarea required name="message" rows="6" />
            </label>
            <button type="submit" className="solid-action">
              Send Inquiry
              <ArrowIcon />
            </button>
            {sent && <p className="form-note">Thank you. Your inquiry has been recorded in this demo site.</p>}
          </form>

          <aside className="contact-sidebar">
            <div className="aside-block">
              <span className="aside-kicker">General Email</span>
              <a href="mailto:contact@kingsbridgellp.com" className="aside-link">
                contact@kingsbridgellp.com
              </a>
            </div>
            <div className="aside-block">
              <span className="aside-kicker">Key Offices</span>
              {offices.slice(0, 4).map((office) => (
                <Link key={office.slug} to={`/locations/${office.slug}`} className="aside-row">
                  {office.city}
                  <ArrowIcon size={14} />
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>
    </>
  );
};

export const DiversityPage = () => (
  <>
    <PageHero
      label="DIVERSITY & INCLUSION"
      title="Different perspectives"
      accent="better judgement"
      description="We are building teams where talent can progress, lead, and be heard across practices, offices, and career stages."
      stats={[
        { value: '4', label: 'Core commitments' },
        { value: '31', label: 'Offices involved' },
        { value: '100%', label: 'Matter teams accountable' }
      ]}
    />

    <section className="page-section">
      <div className="section-container">
        <div className="commitment-list">
          {diversityCommitments.map((commitment, index) => (
            <motion.div key={commitment} className="commitment-item" {...fadeUp} transition={{ duration: 0.6, delay: index * 0.06 }}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <p>{commitment}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  </>
);

const resourceLabels = {
  publications: {
    label: 'PUBLICATIONS',
    title: 'Reference material',
    accent: 'for complex decisions',
    description: 'Guides, trackers, and briefings prepared by Kings Bridge LLP teams.'
  },
  events: {
    label: 'EVENTS',
    title: 'Private briefings',
    accent: 'and client sessions',
    description: 'Roundtables, workshops, and webinars for executives, counsel, and legal operations teams.'
  },
  news: {
    label: 'NEWS',
    title: 'Firm updates',
    accent: 'and recent work',
    description: 'Updates on firm capability, recognition, representative matters, and practice growth.'
  }
};

export const ResourcePage = ({ type }) => {
  const config = resourceLabels[type];
  const items = resources[type] || [];

  if (!config) {
    return <NotFoundContent />;
  }

  return (
    <>
      <PageHero
        label={config.label}
        title={config.title}
        accent={config.accent}
        description={config.description}
      />

      <section className="page-section">
        <div className="section-container resource-list">
          {items.map((item, index) => (
            <motion.article key={item.title} className="resource-item" {...fadeUp} transition={{ duration: 0.6, delay: index * 0.06 }}>
              <div>
                <span className="resource-category">{item.category}</span>
                <h2>{item.title}</h2>
                <p>{item.summary}</p>
              </div>
              <time>{item.date}</time>
            </motion.article>
          ))}
        </div>
      </section>
    </>
  );
};

export const LegalPage = ({ type }) => {
  const page = legalPages[type];

  if (!page) {
    return <NotFoundContent />;
  }

  return (
    <>
      <PageHero
        label="LEGAL"
        title={page.title}
        description={`${page.updated}. This page provides website information for visitors to Kings Bridge LLP.`}
      />

      <section className="page-section">
        <div className="section-container legal-content">
          {page.sections.map((section) => (
            <article key={section.heading}>
              <h2>{section.heading}</h2>
              <p>{section.text}</p>
            </article>
          ))}
        </div>
      </section>
    </>
  );
};

const buildSearchIndex = () => [
  ...practiceAreas.map((item) => ({
    title: item.title,
    summary: item.summary,
    type: 'Practice Area',
    href: `/services/${item.slug}`,
    haystack: [item.title, item.summary, item.description, ...item.specialties, ...item.services].join(' ')
  })),
  ...sectors.map((item) => ({
    title: item.title,
    summary: item.summary,
    type: 'Sector',
    href: `/sectors/${item.slug}`,
    haystack: [item.title, item.summary, item.description, ...item.capabilities, ...item.trends].join(' ')
  })),
  ...insights.map((item) => ({
    title: item.title,
    summary: item.excerpt,
    type: 'Insight',
    href: `/insights/${item.slug}`,
    haystack: [item.title, item.category, item.excerpt, item.author, ...item.body].join(' ')
  })),
  ...offices.map((item) => ({
    title: `${item.city} Office`,
    summary: item.description,
    type: 'Location',
    href: `/locations/${item.slug}`,
    haystack: [item.city, item.country, item.region, item.description, ...item.highlights].join(' ')
  })),
  { title: 'Careers', summary: 'Open roles and life at Kings Bridge LLP.', type: 'Page', href: '/careers', haystack: 'careers jobs recruitment training culture' },
  { title: 'Contact', summary: 'Route a matter or inquiry to the right team.', type: 'Page', href: '/contact', haystack: 'contact inquiry email office phone' }
];

export const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [value, setValue] = useState(query);
  const items = useMemo(buildSearchIndex, []);
  const normalized = query.trim().toLowerCase();
  const results = normalized
    ? items.filter((item) => item.haystack.toLowerCase().includes(normalized))
    : items.slice(0, 8);

  return (
    <>
      <PageHero
        label="SEARCH"
        title="Find people,"
        accent="services, and insights"
        description="Search across the main Kings Bridge LLP site content."
      />

      <section className="page-section">
        <div className="section-container search-layout">
          <form
            className="search-form"
            onSubmit={(event) => {
              event.preventDefault();
              const next = value.trim();
              setSearchParams(next ? { q: next } : {});
            }}
          >
            <input
              type="search"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              placeholder="Search the site"
              aria-label="Search the site"
            />
            <button type="submit" className="solid-action">
              Search
            </button>
          </form>

          <div className="search-results">
            <p className="result-count">
              {normalized ? `${results.length} result${results.length === 1 ? '' : 's'} for "${query}"` : 'Featured pages'}
            </p>
            {results.map((item) => (
              <Link key={`${item.type}-${item.title}`} to={item.href} className="search-result">
                <span>{item.type}</span>
                <h2>{item.title}</h2>
                <p>{item.summary}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export const NotFoundPage = () => <NotFoundContent />;
